import { Form } from '@faceup/ui'
import { Button, Input } from '@faceup/ui-base'
import { ChannelType, INSTITUTION_PASSWORD_VALIDATION, Institution } from '@faceup/utils'
import { useState } from 'react'
import type { UserInstitution } from '../../Contexts/UserContext'
import { useGetShortLinkByReportSourceId } from '../../hooks/useGetShortLinkByReportSourceId'
import { useMedia } from '../../mq'
import { sharedMessages } from '../../Shared/translations'
import { defineMessages, FormattedMessage, useIntl } from '../../TypedIntl'
import useAnalytics from '../../utils/analytics'
import useRegion from '../../utils/useRegion'

const messages = defineMessages({
  companyPasswordLabel: 'FollowUp.SearchInstitution.passwordLabel',
  companyPasswordError: 'FollowUp.SearchInstitution.passwordError',
  companyPasswordPlaceholder: 'FollowUp.SearchInstitution.passwordPlaceholder',
})

type Props = {
  proceed: (props: UserInstitution) => void
}

const SearchCompanyForm = ({ proceed }: Props) => {
  const [loading, setLoading] = useState<boolean>(false)
  const { formatMessage } = useIntl()
  const isLgDown = useMedia('lgDown')
  const { discoverByReportPassword } = useRegion()
  const { track } = useAnalytics()
  const { getShortLinkByReportSourceId } = useGetShortLinkByReportSourceId()

  const [passwordInput, setPasswordInput] = useState({ value: '', error: false })

  const validateForm = () => {
    if (!INSTITUTION_PASSWORD_VALIDATION.test(passwordInput.value.trim())) {
      setPasswordInput({ ...passwordInput, error: true })

      return false
    }

    return true
  }

  return (
    <>
      <Form.Item
        label={<FormattedMessage {...messages.companyPasswordLabel} />}
        errorMessage={
          passwordInput.error && <FormattedMessage {...messages.companyPasswordError} />
        }
        formItemProps={{
          className: 'mb-[6px]',
          'data-test': 'search-company-form-item-password',
        }}
      >
        <Input
          onClick={() =>
            track('Search institution input clicked', { institutionType: Institution.Company })
          }
          autoComplete='off'
          size='large'
          data-test='search-company-form-password'
          placeholder={formatMessage(messages.companyPasswordPlaceholder)}
          onChange={({ target: { value } }) => setPasswordInput({ value, error: false })}
        />
      </Form.Item>
      <Form.Item>
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '16px' }}>
          <Button
            type='primary'
            htmlType='submit'
            block={isLgDown}
            loading={loading}
            data-test='search-company-form-continue'
            onClick={async () => {
              setLoading(true)

              if (!validateForm()) {
                setLoading(false)
                return
              }

              const discovery = await discoverByReportPassword(passwordInput.value.trim())
              if (discovery) {
                const shortLink = await getShortLinkByReportSourceId(
                  discovery.reportSourceId,
                  discovery.institutionId
                )
                if (shortLink) {
                  proceed({
                    institutionId: discovery.institutionId,
                    defaultFormId: discovery.reportSourceId,
                    password: passwordInput.value.trim(),
                    channelType: ChannelType.ReportingSystem,
                    type: discovery.type,
                    shortLink,
                  })
                  setPasswordInput({ value: '', error: false })
                } else {
                  setPasswordInput({ ...passwordInput, error: true })
                }
              } else {
                setPasswordInput({ ...passwordInput, error: true })
              }

              track('Search institution continue button clicked', {
                institutionType: Institution.Company,
              })

              setLoading(false)
            }}
          >
            <FormattedMessage {...sharedMessages.continue} />
          </Button>
        </div>
      </Form.Item>
    </>
  )
}

export default SearchCompanyForm
